import {
  AddMcpConfigProjectRequestConfig,
  ConnectMcpRequestConfig,
  CreateMcpConfigRequestConfig,
  type CreateMcpConfigInput,
  DeleteMcpConfigRequestConfig,
  DisconnectMcpRequestConfig,
  type McpCatalogEntry,
  McpCatalogRequestConfig,
  type McpConfig,
  McpConfigsRequestConfig,
  type McpProbeResult,
  McpProjectConfigsRequestConfig,
  type McpProjectResponse,
  ProbeMcpServerRequestConfig,
  RefreshMcpToolsRequestConfig,
  RemoveMcpConfigProjectRequestConfig,
  type UpdateMcpConfigInput,
  UpdateMcpConfigRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { err, ok, type Result } from "neverthrow";
import { createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { useI18n } from "~/i18n/context";
import { translateIntegrationError } from "~/lib/error-handling";
import { type FeatureAction, trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export type { McpConfig, McpProbeResult, McpTool } from "@storytell/client";

class McpIntegrationError extends Error {
  readonly errorResult: { code?: string | number; message?: string; statusCode?: number };

  constructor(payload: { code?: string | number; message?: string; statusCode?: number }) {
    const message = payload.message ?? "Request failed";
    super(message);
    this.name = "McpIntegrationError";
    this.errorResult = {
      code: payload.code,
      message,
      statusCode: payload.statusCode,
    };
  }
}

const toError = (e: Error): McpIntegrationError => {
  const meta = e as Error & { code?: string; status?: number };
  return new McpIntegrationError({
    code: meta.code ?? meta.status,
    message: e.message,
    statusCode: meta.status,
  });
};

// Registration and deployment are org-level. A member's grant to an oauth
// server is keyed by the project the connect ran in, so connecting,
// disconnecting and refreshing tools name the deployed project explicitly.
export function useMcp() {
  const wire = useWire();
  const client = useStorytellClient();
  const queryClient = useQueryClient();

  const send = async <T>(
    doFetch: () => Promise<Result<T, Error>>,
    action?: FeatureAction<"mcp">
  ): Promise<Result<T, McpIntegrationError>> => {
    if (isServer) return err(new McpIntegrationError({ statusCode: 0 }));
    const res = (await doFetch()).mapErr(toError);
    if (action && res.isOk()) trackFeature({ feature: "mcp", action, via: "button" });
    return res;
  };

  // Registration, deployment and tool edits change what the org list and every
  // project query answer, and those queries are served from cache for a while;
  // drop them all rather than work out which projects a config touched.
  const sendAndInvalidate = async <T>(doFetch: () => Promise<Result<T, Error>>, action?: FeatureAction<"mcp">) => {
    const res = await send(doFetch, action);
    void queryClient.invalidateQueries({ queryKey: ["mcp-project"] });
    void queryClient.invalidateQueries({ queryKey: ["mcp-configs"] });
    return res;
  };

  // The org defaults to the current one; the discovery hints name the org the
  // user owns, since the current one trails an org switch.
  const listConfigs = async (orgId = wire.currentOrgId()): Promise<Result<McpConfig[], McpIntegrationError>> => {
    const res = await send(() =>
      client.fetch(McpConfigsRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined, {
        orgIdOverride: orgId,
      })
    );
    // Grants maps out-of-scope reads to 404, so non-admins get an empty list
    // instead of an error.
    if (res.isErr() && res.error.errorResult.statusCode === 404) return ok([]);
    return res.map((value) => value.integrations);
  };

  const listProjectConfigs = (projectId: string): Promise<Result<McpProjectResponse, McpIntegrationError>> =>
    send(() =>
      client.fetch(McpProjectConfigsRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined, {
        projectIdOverride: projectId,
      })
    );

  const listCatalog = async (orgId: string | undefined): Promise<Result<McpCatalogEntry[], McpIntegrationError>> => {
    const res = await send(() =>
      client.fetch(McpCatalogRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined, {
        orgIdOverride: orgId,
      })
    );
    return res.map((value) => value.entries);
  };

  const probe = (serverUrl: string): Promise<Result<McpProbeResult, McpIntegrationError>> =>
    send(
      () =>
        client.fetch(
          ProbeMcpServerRequestConfig.withBearerToken(wire.auth.token()),
          undefined,
          { serverUrl },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "probe"
    );

  const createConfig = (input: CreateMcpConfigInput): Promise<Result<McpConfig, McpIntegrationError>> =>
    sendAndInvalidate(
      () =>
        client.fetch(CreateMcpConfigRequestConfig.withBearerToken(wire.auth.token()), undefined, input, {
          orgIdOverride: wire.currentOrgId(),
        }),
      input.catalogSlug ? "catalog_add" : "url_add"
    );

  const updateConfig = (
    integrationId: string,
    input: UpdateMcpConfigInput
  ): Promise<Result<McpConfig, McpIntegrationError>> =>
    sendAndInvalidate(
      () =>
        client.fetch(UpdateMcpConfigRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, input, {
          orgIdOverride: wire.currentOrgId(),
        }),
      input.builtinChoice ? "conflict_toggle" : input.tools ? "tools_edit" : undefined
    );

  const deleteConfig = (integrationId: string): Promise<Result<void, McpIntegrationError>> =>
    sendAndInvalidate(() =>
      client.fetch(DeleteMcpConfigRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );

  const addProject = (integrationId: string, projectId: string): Promise<Result<string[], McpIntegrationError>> =>
    sendAndInvalidate(() =>
      client.fetch(
        AddMcpConfigProjectRequestConfig.withBearerToken(wire.auth.token()),
        { integrationId, projectId },
        undefined,
        { orgIdOverride: wire.currentOrgId() }
      )
    ).then((res) => res.map((value) => value.projects));

  const removeProject = (integrationId: string, projectId: string): Promise<Result<string[], McpIntegrationError>> =>
    sendAndInvalidate(() =>
      client.fetch(
        RemoveMcpConfigProjectRequestConfig.withBearerToken(wire.auth.token()),
        { integrationId, projectId },
        undefined,
        { orgIdOverride: wire.currentOrgId() }
      )
    ).then((res) => res.map((value) => value.projects));

  // For an oauth server the backend lists tools with the caller's grant in
  // this project and answers 409 when there is none.
  const refreshTools = (integrationId: string, projectId: string): Promise<Result<McpConfig, McpIntegrationError>> =>
    sendAndInvalidate(
      () =>
        client.fetch(RefreshMcpToolsRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
          projectIdOverride: projectId,
        }),
      "refresh_tools"
    );

  // The consent screen is a full-page trip, so the current page rides along
  // and the callback lands back on it. Resolves only on failure: success
  // leaves the page.
  const connect = async (integrationId: string, projectId: string): Promise<Result<void, McpIntegrationError>> => {
    const res = await send(() =>
      client.fetch(
        ConnectMcpRequestConfig.withBearerToken(wire.auth.token()),
        { integrationId, returnUrl: window.location.pathname + window.location.search },
        undefined,
        { projectIdOverride: projectId }
      )
    );
    if (res.isErr()) return err(res.error);
    window.location.assign(res.value.authUrl);
    return ok(undefined);
  };

  const disconnect = (integrationId: string, projectId: string): Promise<Result<void, McpIntegrationError>> =>
    send(
      () =>
        client.fetch(DisconnectMcpRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
          projectIdOverride: projectId,
        }),
      "account_disconnect"
    );

  return {
    listConfigs,
    listProjectConfigs,
    listCatalog,
    probe,
    createConfig,
    updateConfig,
    deleteConfig,
    addProject,
    removeProject,
    refreshTools,
    connect,
    disconnect,
  };
}

// Connects the user's own account to a server in the current project. Success
// leaves the page for the consent screen, so the busy id stays set.
export function useMcpConnect() {
  const wire = useWire();
  const { t } = useI18n();
  const mcp = useMcp();
  const [busyId, setBusyId] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const connect = async (serverId: string) => {
    const projectId = wire.currentProjectId();
    if (!projectId) return;
    setError(null);
    setBusyId(serverId);
    const result = await mcp.connect(serverId, projectId);
    if (result.isErr()) {
      setError(translateIntegrationError(result.error, t, "mcp_integration"));
      setBusyId(null);
    }
  };

  return { connect, busyId, error };
}

// The servers deployed to one project, split by whether the caller can call
// them, with the caller's own connection on each. The prompt bar, the
// auto-approve controls and the settings page all mount this, so the answer
// is served from cache between them; edits made here invalidate it, and the
// OAuth callback is a full page load that starts from an empty cache.
export function useMcpProject(projectId: () => string | undefined) {
  const wire = useWire();
  const mcp = useMcp();

  const query = useQuery(() => ({
    queryKey: ["mcp-project", projectId()],
    enabled: !isServer && wire.auth.isReady() && !!projectId(),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const result = await mcp.listProjectConfigs(projectId() ?? "");
      if (result.isErr()) throw result.error;
      return result.value;
    },
  }));

  // Reading data while the query has none suspends the nearest boundary; the
  // accessors answer empty instead.
  const page = () => (query.isSuccess || query.isRefetchError ? query.data : undefined);

  return {
    servers: () => page()?.servers ?? [],
    unconnected: () => page()?.unconnected ?? [],
    isLoading: () => query.isPending,
    error: () => query.error,
    refetch: () => query.refetch(),
  };
}

// The org Integrations page renders the catalog on load and the discovery
// hints count it, so every surface shares one cached copy. Reads go through
// isSuccess: reading data any earlier touches the query's resource, which
// suspends the root boundary (boot splash, scroll lost) on a page with no
// boundary of its own.
export function useMcpCatalog(orgId: () => string | undefined) {
  const wire = useWire();
  const mcp = useMcp();

  const query = useQuery(() => {
    const id = orgId();
    return {
      queryKey: ["mcp-catalog", id],
      enabled: !isServer && wire.auth.isReady() && !!id,
      staleTime: 1000 * 60 * 30,
      queryFn: async () => {
        const result = await mcp.listCatalog(id);
        if (result.isErr()) throw result.error;
        return result.value;
      },
    };
  });

  return {
    entries: () => (query.isSuccess ? query.data : undefined),
    isLoading: () => query.isLoading,
  };
}

// The org's registered servers, for counting what is left in the catalog
// away from the Integrations page, which keeps its own editable list. Members
// read an empty list, so callers pass an org only when the user owns it.
export function useMcpConfigs(orgId: () => string | undefined) {
  const wire = useWire();
  const mcp = useMcp();

  const query = useQuery(() => {
    const id = orgId();
    return {
      queryKey: ["mcp-configs", id],
      enabled: !isServer && wire.auth.isReady() && !!id,
      staleTime: 1000 * 60 * 5,
      queryFn: async () => {
        const result = await mcp.listConfigs(id);
        if (result.isErr()) throw result.error;
        return result.value;
      },
    };
  });

  return { configs: () => (query.isSuccess ? query.data : undefined) };
}
